<script setup lang="ts">
import {DetectionNotificationProps} from "@/notification/interfaces";
import {computed, reactive, ref} from "vue";
import {Poll, PollEntry} from "@/program/interfaces/Program";
import Consumer from "@/core/interfaces/consumers/Consumer";
import {useNotificationStore} from "@/stores/notification.store";
import usePollSubmission from "@/program/composables/usePollSubmission";

const props = defineProps<{
    item: DetectionNotificationProps;
}>();

const {hiddenPolls} = useNotificationStore();
const {submitPollEntry, cancelPollEntry} = usePollSubmission();

const selectedEntry = reactive<Record<number, PollEntry>>({});
const actionResponses = ref<object[]>();

/** The polls displayed at the top of the notification; the others belong to a program card. */
const polls = computed(() => props.item.polls?.filter(poll => !poll.display_in_program) ?? []);

/**
 * Submit a poll entry for a given poll and customer
 * @param poll  The poll for which the entry is being submitted
 * @param entry  The poll entry being submitted
 * @param customer  The customer to submit the poll entry for
 */
const submit = async (poll: Poll, entry: PollEntry, customer: Consumer) => {
    hiddenPolls[poll.id] = true;
    selectedEntry[poll.id] = JSON.parse(JSON.stringify(entry));

    actionResponses.value = await submitPollEntry(poll, entry, customer);
};

/**
 * Cancel a poll entry for a given poll and customer
 * @param poll  The poll for which the entry is being cancelled
 * @param customer  The customer to cancel the poll entry for
 */
const cancel = async (poll: Poll, customer: Consumer) => {
    hiddenPolls[poll.id] = false;

    await cancelPollEntry(poll, selectedEntry[poll.id], customer, actionResponses.value);

    delete selectedEntry[poll.id];
};

</script>

<template>
  <div v-if="item.customer" class="flex flex-col">
    <template v-for="poll in polls" :key="poll.id">
      <div class="text-sm font-semibold text-primary">
        {{ poll.title || poll.name }}
      </div>
      <div
        v-if="!hiddenPolls[poll.id]"
        class="text-sm font-semibold"
      >
        <div class="flex gap-2 pt-1">
          <button
            v-for="entry in poll.entries"
            :key="entry.id"
            class="flex items-center justify-center rounded-xl bg-primary px-2 h-7"
            @click="() => submit(poll, entry, item.customer!)"
          >
            {{ entry.label }}
          </button>
        </div>
      </div>
      <div v-if="hiddenPolls[poll.id]" class="text-sm">
        <div>
          Réponse enregistrée !
          <button class="text-primary underline" @click="() => cancel(poll, item.customer!)">
            Annuler
          </button>
        </div>
        <div class="tracking-tight">
          {{ poll.feedback }}
        </div>
      </div>
    </template>
  </div>
</template>
